import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetTenantPlanQuery } from "../features/tenants/tenantsApiSlice";
import useAuth from "./useAuth";
import {
  FEATURE_KEYS,
  PLAN_IDS,
  getPlanOptionById,
  getRequiredPlanForFeature,
  getUpgradePlanForFeature,
  hasFeatureAccess,
  normalizePlanId,
} from "../utils/planFeatures";

export default function usePlanGate(featureKey, options = {}) {
  const { planId, skipTenantQuery = false, eDojangOverrideEnabled } = options;
  const shouldLoadTenant = !planId && !skipTenantQuery;
  const { data: tenant, isLoading: isTenantLoading } =
    useGetTenantPlanQuery(undefined, {
      skip: !shouldLoadTenant,
    });
  const { isSuperAdmin } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();

  const currentPlanId = normalizePlanId(
    planId || tenant?.billingPlan || PLAN_IDS.BASIS,
  );
  const requiredPlanId = getRequiredPlanForFeature(featureKey);
  const requiredPlanLabel = getPlanOptionById(requiredPlanId)?.title || "";
  const upgradePlanId = getUpgradePlanForFeature(currentPlanId, featureKey);
  const effectiveEDojangOverride =
    typeof eDojangOverrideEnabled === "boolean"
      ? eDojangOverrideEnabled
      : Boolean(tenant?.featureOverrides?.eDojang);
  const isEDojangOverrideAllowed =
    featureKey === FEATURE_KEYS.E_DOJANG &&
    (isSuperAdmin || effectiveEDojangOverride);
  const isAllowed = isEDojangOverrideAllowed || hasFeatureAccess(currentPlanId, featureKey);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  const openDialog = React.useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const closeDialog = React.useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const goToUpgrade = React.useCallback(() => {
    setIsDialogOpen(false);
    navigate(`/dashboard/settings/modules?upgrade=${upgradePlanId}`, {
      state: {
        from: location.pathname,
        featureKey,
      },
    });
  }, [navigate, upgradePlanId, location.pathname, featureKey]);

  return {
    isAllowed,
    isLoading: shouldLoadTenant ? isTenantLoading : false,
    isDialogOpen,
    currentPlanId,
    requiredPlanId,
    requiredPlanLabel,
    openDialog,
    closeDialog,
    goToUpgrade,
  };
}
